import React, { useEffect, useState, useMemo } from 'react'
import { Select, Grid } from '@components/common'
import { useFetchPeriodsQuery, useGetYearsQuery } from '@api'
import { Period } from '@api/models'
import moment from 'moment'

interface PeriodRangeSelectorProps {
  assetCompanyID: number
  fromDate: string
  toDate: string
  onFromDateChange: (date: string) => void
  onToDateChange: (date: string) => void
  disabled?: boolean
  readonly?: boolean
  errors?: {
    SP_YMO_FRM?: { message?: string }
    SP_YMO_TO?: { message?: string }
  }
}

interface PeriodOption {
  label: string
  value: string
  startDate: string
  period: Period
}

const PeriodRangeSelector: React.FC<PeriodRangeSelectorProps> = ({
  assetCompanyID,
  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,
  disabled = false,
  readonly = false,
  errors = {},
}) => {
  const { data: years = [], isLoading: isLoadingYears } = useGetYearsQuery({ assetCompanyID })

  const [fromYear, setFromYear] = useState<number | null>(null)
  const [toYear, setToYear] = useState<number | null>(null)
  const [hasInitialized, setHasInitialized] = useState(false)

  //Clear dates on load if they are default dates 
  useEffect(() => {
    if (!hasInitialized) {
      onFromDateChange('')
      onToDateChange('')
      setHasInitialized(true)
    }
  }, [hasInitialized, onFromDateChange, onToDateChange])

  const { data: fromYearPeriods = [], isLoading: isLoadingFromPeriods } = useFetchPeriodsQuery(
    { assetCompanyID, year: fromYear! },
    { skip: !fromYear }
  )

  const { data: toYearPeriods = [], isLoading: isLoadingToPeriods } = useFetchPeriodsQuery(
    { assetCompanyID, year: toYear! },
    { skip: !toYear || toYear === fromYear }
  )

  const createYearOptions = (yearsList: number[]) => 
    yearsList.map(year => ({ label: year.toString(), value: year }))

  const createPeriodOptions = (periods: Period[]): PeriodOption[] => 
    periods.map(period => ({
      label: `Period ${period.CP_PERIOD} (${moment(period.CP_START_DATE).format('MMM DD')} - ${moment(period.CP_DATE).format('MMM DD, YYYY')})`,
      value: period.CP_DATE,
      startDate: period.CP_START_DATE,
      period,
    }))

  const yearOptions = useMemo(() => createYearOptions(years), [years])
  
  const toYearOptions = useMemo(() => 
    fromYear ? yearOptions.filter(option => option.value >= fromYear) : yearOptions,
    [yearOptions, fromYear]
  )

  const fromPeriodOptions = useMemo(() => {
    if (!fromYear || !toYear) return []
    return createPeriodOptions(fromYearPeriods)
  }, [fromYearPeriods, fromYear, toYear])

  const toPeriodOptions = useMemo(() => {
    if (!fromYear || !toYear) return []
    
    const periods = fromYear === toYear ? fromYearPeriods : toYearPeriods
    
    if (fromYear === toYear && fromDate) {
      return createPeriodOptions(
        periods.filter(period => moment(period.CP_DATE).isAfter(moment(fromDate)))
      )
    }
    
    return createPeriodOptions(periods)
  }, [fromYearPeriods, toYearPeriods, fromYear, toYear, fromDate])

  const findSelectedOption = (options: PeriodOption[], date: string, matchStartDate = false) => {
    if (!date || options.length === 0) return null
    
    const selected = options.find(opt => 
      matchStartDate ? (opt.startDate === date || opt.value === date) : opt.value === date
    )
    
    return selected ? { label: selected.label, value: selected.value } : null
  }

  const fromPeriodValue = useMemo(() => 
    findSelectedOption(fromPeriodOptions, fromDate, true),
    [fromDate, fromPeriodOptions]
  )

  const toPeriodValue = useMemo(() => 
    findSelectedOption(toPeriodOptions, toDate),
    [toDate, toPeriodOptions]
  )
  const handleYearChange = (
    option: { label: string; value: number },
    setYear: (year: number) => void,
    resetDate: () => void,
    isFromYear = false
  ) => {
    setYear(option.value)
    resetDate()
    
    if (isFromYear && toYear && toYear < option.value) {
      setToYear(option.value)
      onToDateChange('')
    }
  }

  const handlePeriodChange = (
    option: PeriodOption,
    onChange: (date: string) => void,
    useStartDate = false
  ) => {
    const date = useStartDate ? option.startDate : option.value
    onChange(date)
    
    if (useStartDate && toDate && moment(toDate).isBefore(moment(date))) {
      onToDateChange('')
    }
  }

  const isLoadingPeriods = isLoadingFromPeriods || isLoadingToPeriods

  const createSelectProps = (
    label: string,
    name: string,
    options: any[],
    value: any,
    onChange: (option: any) => void,
    extraDisabled = false,
    error?: boolean,
    errorMsg?: string
  ) => ({
    label,
    name,
    options,
    value,
    onChange,
    disabled: disabled || extraDisabled,
    readOnly: readonly,
    placeholder: isLoadingPeriods && name.includes('Period') ? "Loading periods..." : `Select ${label.toLowerCase()}`,
    type: 'autocomplete' as const,
    isClearable: false,
    error,
    errorMsg,
  })

  return (
    <>
      <Grid.Item m={6}>
        <Select
          {...createSelectProps(
            "From Year",
            "fromYear",
            yearOptions,
            fromYear ? { label: fromYear.toString(), value: fromYear } : null,
            (opt) => handleYearChange(opt, setFromYear, () => onFromDateChange(''), true),
            isLoadingYears
          )}
        />
      </Grid.Item>
      
      <Grid.Item m={6}>
        <Select
          {...createSelectProps(
            "To Year",
            "toYear",
            toYearOptions,
            toYear ? { label: toYear.toString(), value: toYear } : null,
            (opt) => handleYearChange(opt, setToYear, () => onToDateChange('')),
            isLoadingYears || !fromYear
          )}
        />
      </Grid.Item>
      
      <Grid.Item m={6}>
        <Select
          {...createSelectProps(
            "From Period",
            "fromPeriod",
            fromPeriodOptions,
            fromPeriodValue,
            (opt) => handlePeriodChange(opt, onFromDateChange, true),
            !fromYear || !toYear || isLoadingPeriods || fromPeriodOptions.length === 0,
            !!errors.SP_YMO_FRM,
            errors.SP_YMO_FRM?.message
          )}
        />
      </Grid.Item>
      
      <Grid.Item m={6}>
        <Select
          {...createSelectProps(
            "To Period",
            "toPeriod",
            toPeriodOptions,
            toPeriodValue,
            (opt) => handlePeriodChange(opt, onToDateChange),
            !toYear || !fromDate || isLoadingPeriods || toPeriodOptions.length === 0,
            !!errors.SP_YMO_TO,
            errors.SP_YMO_TO?.message
          )}
        />
      </Grid.Item>
    </>
  )
}

export default PeriodRangeSelector